import { useState } from 'react'
import { Box, InputBase, Typography } from '@mui/material'
import { Prescription } from '../../../../types/models/Presc'
import { formatTimeInJP } from '../../../../helpers'
import { colorTokens } from '../../../../shared/styles/theme'
import { RADIUS } from '../../../../shared/styles/radii'
import { typeScale } from '../../../../shared/styles/typography'
import { HospitalFormatSetting } from '../../apis/formatSetting'
import { fullHospitalCodeOf } from './hospitalCode'
import { PillSelect } from './PillSelect'
import {
  SAVE_DEBOUNCE_MS,
  useFormatAnnotationSaver,
  useHospitalFormatSettingQuery,
} from './useFormatSetting'

/**
 * 右ペインの「フォーマット設定」タブ。**この面で唯一の書き込み。**
 *
 * 紙（左の画像）を見て、この医療機関の様式を 9 項目選ぶ。選ぶたびに debounce して編集ログへ 1 行追記し、
 * 保存ボタンは置かない（引き算）。項目・選択肢は backend の items[] をそのまま描き、front は語彙を持たない。
 *
 * 現在値の出所は 2 つ: hospital_setting.json の現行値（current）と、編集ログの機関の最新行（draft）。
 * 開いた時点のフォームは draft ?? current で埋める。**同じ機関の別の紙で判断済みなら、その値が出る**
 * （様式は機関の属性）— どの紙での判断かは見出し下の 1 行で言う。
 */

const LABEL_WIDTH = 116

const Heading = ({ children }: { children: string }) => (
  <Typography
    sx={{ mt: 2.5, mb: 0.5, fontSize: 12, color: colorTokens.fg.faint }}
  >
    {children}
  </Typography>
)

const Note = ({ children }: { children: string }) => (
  <Typography sx={{ py: 1, fontSize: 14, color: colorTokens.fg.muted }}>
    {children}
  </Typography>
)

interface Props {
  prescription: Prescription
  /** テストで debounce を短くするためだけに開けている */
  saveDebounceMs?: number
}

export const FormatSettingPane = ({
  prescription,
  saveDebounceMs = SAVE_DEBOUNCE_MS,
}: Props) => {
  const code = fullHospitalCodeOf(prescription)
  const { data, isPending, isError } = useHospitalFormatSettingQuery(code)

  return (
    <Box sx={{ py: 2, px: 3 }}>
      {!code ? (
        // empty-states.md 型: 理由を 1 文。機関が分からなければ様式のしようがない
        <Note>医療機関が読み取れていないので、様式を判断できません</Note>
      ) : isError ? (
        <Note>様式を読み込めませんでした</Note>
      ) : isPending || !data ? null : (
        // key=code: 同じ機関の間は再取得で入力を巻き戻さない。機関が変わったら組み直す
        <FormatSettingForm
          key={code}
          ocrRequestEventId={prescription.ocr_request_event_id}
          setting={data}
          saveDebounceMs={saveDebounceMs}
        />
      )}
    </Box>
  )
}

const FormatSettingForm = ({
  ocrRequestEventId,
  setting,
  saveDebounceMs,
}: {
  ocrRequestEventId: string
  setting: HospitalFormatSetting
  saveDebounceMs: number
}) => {
  const [values, setValues] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(
      setting.items.map((item) => [item.key, item.draft ?? item.current])
    )
  )
  const [memo, setMemo] = useState(setting.memo)
  const { save, savedAt, error } = useFormatAnnotationSaver(
    ocrRequestEventId,
    setting.full_hospital_code,
    saveDebounceMs
  )

  const commit = (
    nextValues: Record<string, string | null>,
    nextMemo: string
  ) => {
    setValues(nextValues)
    setMemo(nextMemo)
    save({ values: nextValues, memo: nextMemo })
  }

  const latest = setting.latest_annotation
  const provenance = latest
    ? `${formatTimeInJP(latest.created_at)} ${
        latest.ocr_request_event_id === ocrRequestEventId
          ? 'この紙で判断'
          : '別の紙で判断'
      }`
    : '未判断'

  // 見出し（表記 / 保険欄）は backend の items[].group で束ねる。並びは最初に現れた順
  const groups = setting.items.reduce<string[]>(
    (acc, item) => (acc.includes(item.group) ? acc : [...acc, item.group]),
    []
  )

  const renderItems = (group: string) =>
    setting.items
      .filter((item) => item.group === group)
      .map((item) => {
        const value = values[item.key] ?? null
        const differsFromCurrent =
          item.current !== null && value !== null && value !== item.current
        return (
          <Box
            key={item.key}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.25 }}
          >
            <Typography
              sx={{
                width: LABEL_WIDTH,
                flexShrink: 0,
                fontSize: 13,
                color: colorTokens.fg.muted,
              }}
            >
              {item.label}
            </Typography>
            <Box sx={{ ml: -1.25 }}>
              <PillSelect
                ariaLabel={item.label}
                value={value}
                choices={item.choices}
                onChange={(next) =>
                  commit({ ...values, [item.key]: next }, memo)
                }
              />
            </Box>
            {/* 現行 json と違う値を選んでいるときだけ、何から変えたかを添える */}
            {differsFromCurrent && (
              <Typography
                noWrap
                sx={{ ...typeScale.tag, color: colorTokens.fg.faint }}
              >
                現行 {item.current}
              </Typography>
            )}
          </Box>
        )
      })

  return (
    <Box>
      <Typography sx={{ fontSize: 14, color: colorTokens.fg.default }}>
        {setting.hospital_name || '（名称なし）'}
      </Typography>
      <Typography sx={{ ...typeScale.tag, color: colorTokens.fg.faint }}>
        {`${setting.full_hospital_code} ・ ${provenance}`}
      </Typography>

      {groups.map((group) => (
        <Box key={group}>
          <Heading>{group}</Heading>
          {renderItems(group)}
        </Box>
      ))}

      <Heading>メモ</Heading>
      <InputBase
        multiline
        minRows={2}
        value={memo}
        onChange={(event) => commit(values, event.target.value)}
        placeholder="メモ"
        inputProps={{ 'aria-label': 'メモ' }}
        sx={{
          width: '100%',
          px: 1,
          py: 0.5,
          fontSize: 14,
          borderRadius: `${RADIUS.md}px`,
          border: `1px solid ${colorTokens.border.subtle}`,
          '&.Mui-focused': { boxShadow: colorTokens.focus.ringNeutral },
        }}
      />

      {/* 保存の状態は 1 行だけ。ボタンは無い */}
      {(savedAt || error) && (
        <Typography
          sx={{ mt: 1.5, ...typeScale.tag, color: colorTokens.fg.faint }}
        >
          {error || `${formatTimeInJP(savedAt as string)} 保存`}
        </Typography>
      )}
    </Box>
  )
}
